import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Modal, Popconfirm, Switch, Table } from "antd";
import { Body } from "../../../components/main-body/body";
import useNotification from "../../../components/notification/notification";
import ApiMcp from "../../../api/mcp";
import { selectCurrentProject } from "../../../store/slices/projects";
import PhaseSelect, { WIDEN_TO_DRAFTS, isWider } from "../ai-connectors/phase-select";

import "../authentication/authentication.css";
import "../ai-connectors/ai-connectors.css";

const TURN_ON = {
  title: "Allow AI assistants in this project?",
  content:
    "Users whose role has \"Use AI connectors\" will be able to give their Claude or ChatGPT assistant read "
    + "access to this project's content. What the assistant reads is sent to Anthropic or OpenAI.",
  okText: "Allow",
};

const TURN_OFF = {
  title: "Stop all AI calls in this project?",
  content: "Connections are kept and work again when you allow AI assistants again.",
  okText: "Stop",
  okButtonProps: { danger: true },
};

const utc = (value) => (value ? new Date(value).toISOString().slice(0, 16).replace("T", " ") : null);

/**
 * LF360-2100 stage 1 — AI connectors in one project, for Organization admins.
 *
 * The project's own switch, under the organization's (Administration → AI
 * connectors, System admins): off by default, and off is this project's kill
 * switch — every AI call here is refused from the next request, connections
 * are kept. Below it, everybody's live connections to the project, each of
 * which can be revoked. Who may connect at all is the "Use AI connectors"
 * permission on the Roles screen.
 */
const ProjectAiConnectors = () => {
  const { openNotification } = useNotification();
  const notifyRef = useRef(openNotification);
  notifyRef.current = openNotification;

  const [modal, modalContext] = Modal.useModal();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  // The requests go to the open project (X-Project-Id), and switching projects
  // keeps this screen mounted. So what is shown belongs to one opening of a
  // project, numbered by `viewRef`: every project change starts a new one,
  // clears the screen and reads it again, and an answer is applied only in the
  // opening it was asked in. A project id alone is not enough: after A → B → A
  // it repeats, and a slow answer from the first A would overwrite the second.
  const projectId = useSelector(selectCurrentProject)?.id;
  const viewRef = useRef(0);

  // `apply`, only while the opening a request was made in is still the current one.
  const inView = (view, apply) => (...args) => (viewRef.current === view ? apply(...args) : undefined);

  // Reads are numbered too: only the latest one is shown. The list is read
  // again after a revoke, and an earlier read that answers late would bring
  // the revoked connection back. A new opening ends every read before it.
  const lastLoadRef = useRef(0);

  const load = useCallback(() => {
    lastLoadRef.current += 1;
    const asked = lastLoadRef.current;
    const ifLatest = (apply) => (...args) => (lastLoadRef.current === asked ? apply(...args) : undefined);
    return ApiMcp.getProjectSettings()
      .then(ifLatest((res) => setSettings(res.data.mcp)))
      .catch(ifLatest(() => notifyRef.current({
        type: "error", message: <p>Failed to load AI connector settings</p>,
      })));
  }, []);

  // A confirmation is asked about one project. One still open when the project
  // changes is closed: answering it would otherwise switch the new project.
  const confirmRef = useRef(null);

  useEffect(() => {
    viewRef.current += 1;
    lastLoadRef.current += 1;
    setSettings(null);
    setSaving(false);
    if (confirmRef.current) {
      confirmRef.current.destroy();
      confirmRef.current = null;
    }
    if (projectId) load();
  }, [projectId, load]);

  // `changes`: only what is being changed; the server leaves the rest.
  // `askedIn` is the opening the question was asked in; nothing is sent unless
  // it is still the current one, whose project the request goes to.
  const save = (changes, askedIn) => {
    if (viewRef.current !== askedIn) return undefined;

    setSaving(true);
    return ApiMcp.updateProjectSettings(changes)
      .then(inView(askedIn, (res) => {
        setSettings((current) => ({ ...current, ...res.data.mcp }));
        notifyRef.current({ type: "success", message: <p>AI connector settings saved</p> });
      }))
      .catch(inView(askedIn, () => notifyRef.current({
        type: "error", message: <p>Failed to save AI connector settings</p>,
      })))
      .finally(inView(askedIn, () => setSaving(false)));
  };

  const ask = (question, changes) => {
    const askedIn = viewRef.current;
    confirmRef.current = modal.confirm({
      ...question,
      cancelText: "Cancel",
      onOk: () => save(changes, askedIn),
      afterClose: () => {
        confirmRef.current = null;
      },
    });
  };

  const toggle = (mcpEnabled) => ask(mcpEnabled ? TURN_ON : TURN_OFF, { mcpEnabled });

  // Widening asks first; narrowing is saved at once.
  const changePhase = (maxPhase) => (isWider(settings.maxPhase, maxPhase)
    ? ask(WIDEN_TO_DRAFTS, { maxPhase })
    : save({ maxPhase }, viewRef.current));

  const revoke = (connection) => {
    const view = viewRef.current;
    return ApiMcp.revokeProjectConnection(connection.id)
      .then(inView(view, () => {
        notifyRef.current({ type: "success", message: <p>Access revoked</p> });
        return load();
      }))
      .catch(inView(view, () => notifyRef.current({
        type: "error", message: <p>Failed to revoke access</p>,
      })));
  };

  const columns = [
    {
      title: "User",
      key: "user",
      render: (_, row) => (
        <>
          <div>{row.user.name || row.user.email || row.user.id}</div>
          {row.user.name && <div className="ai-connectors__text">{row.user.email}</div>}
        </>
      ),
    },
    { title: "Assistant", key: "client", render: (_, row) => `${row.client.name || row.client.host} (${row.client.host})` },
    { title: "Scopes", key: "scopes", render: (_, row) => row.scopes.join(", ") },
    { title: "Last used (UTC)", key: "lastUsedAt", render: (_, row) => utc(row.lastUsedAt) || "Never" },
    { title: "Expires (UTC)", key: "expiresAt", render: (_, row) => utc(row.expiresAt) },
    {
      title: "",
      key: "actions",
      render: (_, row) => (
        <Popconfirm
          title={`Revoke access for ${row.client.name || row.client.host}?`}
          description="The assistant loses access to this project immediately."
          okText="Revoke"
          okButtonProps={{ danger: true }}
          onConfirm={() => revoke(row)}
        >
          <Button danger>Revoke</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Body scroll header={{ title: "AI connectors" }}>
      {modalContext}
      <div className="auth-portal-settings">
        <div className="profile_form-container">
          <div className="authps-section authps-pad">
            <div className="authps-row">
              <div className="authps-label">AI assistants allowed in this project</div>
              <div className="authps-value">
                <Switch
                  aria-label="AI assistants allowed in this project"
                  checked={Boolean(settings?.mcpEnabled)}
                  disabled={!settings}
                  loading={saving}
                  onChange={toggle}
                />
              </div>
            </div>
            {settings && !settings.systemEnabled && (
              <p className="ai-connectors__text" role="status">
                AI connectors are off for the whole organization. A System admin turns them on under
                Administration → AI connectors (MCP); until then nothing connects here either.
              </p>
            )}
            <p className="ai-connectors__text">
              An assistant reads only what its user can see here, and changes nothing unless the maximum phase
              below allows drafts. Who may connect is the "Use AI connectors" permission on{" "}
              <Link to="../roles">Roles and permissions</Link>; every call is in Analytics → Audit trail → AI calls.
            </p>
          </div>

          <div className="authps-divider" />

          <div className="authps-section authps-pad">
            <div className="authps-row">
              <div className="authps-label">Maximum phase for this project</div>
              <div className="authps-value">
                <PhaseSelect
                  label="Maximum phase for this project"
                  value={settings?.maxPhase}
                  phases={settings?.phases}
                  limit={settings?.organizationMaxPhase}
                  disabled={!settings}
                  loading={saving}
                  onChange={changePhase}
                />
              </div>
            </div>
            <p className="ai-connectors__text">
              Drafts lets the assistants of Organization and System admins create and edit drafts here, with the
              Draft scope given on the consent screen. Nothing reaches subjects until someone publishes it. Lowering
              it stops the next write and deletes nothing.
            </p>
          </div>

          <div className="authps-divider" />

          <div className="authps-section authps-pad">
            <div className="authps-label">Connections to this project</div>
            <Table
              rowKey="id"
              loading={!settings}
              dataSource={settings?.connections || []}
              columns={columns}
              pagination={false}
              locale={{ emptyText: "No AI assistant is connected to this project" }}
            />
          </div>
        </div>
      </div>
    </Body>
  );
};

export default ProjectAiConnectors;
